import React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Trophy } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { Table } from "@/components/ui/table";
import { ReviewOverviewSnapshot, ReviewSnapshotParams, ReviewSnapshotSort } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { PlayersExportButton } from "./PlayersExportButton";
import { positiveSentimentPercent, replyRateFromSnapshot } from "./snapshotHelpers";

type PlayerSortKey = ReviewSnapshotSort;

const SORT_COLUMNS: {
  key: PlayerSortKey;
  label: string;
}[] = [
  { key: "volume", label: "Reviews" },
  { key: "star_rating", label: "Star avg" },
  { key: "nps", label: "NPS avg" },
  { key: "ten_point", label: "10-pt avg" },
  { key: "sentiment", label: "Sentiment" },
  { key: "replies", label: "Replies" },
];

const TROPHY_COLORS = {
  1: "#FFD700",
  2: "#CCCCCC",
  3: "#E0B68A",
} as const;

function metricValue(snapshot: ReviewOverviewSnapshot, key: PlayerSortKey): number | null {
  switch (key) {
    case "volume":
      return snapshot.totalReviews;
    case "star_rating":
      return snapshot.totalStarReviews > 0 ? snapshot.avgStarRating : null;
    case "nps":
      return snapshot.totalNpsResponses > 0 ? snapshot.avgNpsScore : null;
    case "ten_point":
      return snapshot.totalTenPointResponses > 0 ? snapshot.avgTenPointScore : null;
    case "sentiment":
      return positiveSentimentPercent(snapshot);
    case "replies":
      return replyRateFromSnapshot(snapshot);
  }
}

function formatMetric(snapshot: ReviewOverviewSnapshot, key: PlayerSortKey): string {
  const value = metricValue(snapshot, key);
  if (value == null) return "—";
  if (key === "volume") return value.toLocaleString();
  if (key === "sentiment" || key === "replies") return `${value.toFixed(0)}%`;
  return value.toFixed(1);
}

function RankTrophy({ rank }: { rank: number }) {
  if (rank < 1 || rank > 3) return null;
  const color = TROPHY_COLORS[rank as 1 | 2 | 3];
  return (
    <span className="relative inline-flex items-center" title={`#${rank}`} aria-label={`Rank ${rank}`}>
      <Trophy className="h-4 w-4" style={{ color }} fill={color} />
      <span className="absolute inset-0 flex items-center justify-center pt-0.5 text-[9px] font-bold text-foreground">
        {rank}
      </span>
    </span>
  );
}

export function PlayersTable({
  snapshots,
  sort,
  order,
  loading = false,
  totalCount,
  filterLabels,
  toExportParams,
  onSortChange,
  onPlayerClick,
}: {
  snapshots?: ReviewOverviewSnapshot[];
  sort: PlayerSortKey;
  order: "asc" | "desc";
  loading?: boolean;
  totalCount?: number | null;
  filterLabels?: string[];
  toExportParams: () => ReviewSnapshotParams;
  onSortChange: (sort: PlayerSortKey, order: "asc" | "desc") => void;
  onPlayerClick?: (playerId: number) => void;
}) {
  const rows = React.useMemo(() => (snapshots ?? []).filter(snapshot => snapshot.playerId != null), [snapshots]);

  if (rows.length === 0) return null;

  const toggleSort = (key: PlayerSortKey) => {
    if (loading) return;
    if (sort === key) {
      onSortChange(key, order === "desc" ? "asc" : "desc");
    } else {
      onSortChange(key, "desc");
    }
  };

  return (
    <section className="font-sans" aria-busy={loading}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight mb-0">Player performance</h2>
        <PlayersExportButton
          toQueryParams={toExportParams}
          filterLabels={filterLabels}
          totalCount={totalCount}
          loading={loading}
          disabled={!totalCount}
        />
      </div>

      <div
        className={cn(
          "overflow-hidden rounded-2xl border border-border/70 transition-opacity",
          loading && "pointer-events-none opacity-50"
        )}
      >
        <Table>
          <Table.Header>
            <Table.Row className="hover:bg-transparent">
              <Table.Head className="w-[28%]">Player</Table.Head>
              {SORT_COLUMNS.map(column => {
                const active = sort === column.key;
                const Icon = !active ? ArrowUpDown : order === "desc" ? ArrowDown : ArrowUp;
                return (
                  <Table.Head key={column.key} className="text-right">
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      disabled={loading}
                      className={cn(
                        "inline-flex items-center gap-1 font-semibold",
                        loading ? "cursor-not-allowed" : "cursor-pointer",
                        active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {column.label}
                      <Icon className="h-3.5 w-3.5" />
                    </button>
                  </Table.Head>
                );
              })}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((snapshot, index) => {
              const player = snapshot.player;
              const name = player?.fullName || "Unknown Player";
              const photoUrl = player?.photoUrl;
              const sortRank = order === "desc" && metricValue(snapshot, sort) != null ? index + 1 : null;

              return (
                <Table.Row key={snapshot.id}>
                  <Table.Cell>
                    <button
                      type="button"
                      disabled={loading || !snapshot.playerId || !onPlayerClick}
                      onClick={() => snapshot.playerId && onPlayerClick?.(snapshot.playerId)}
                      className="flex w-full min-w-0 items-center gap-2.5 text-left disabled:cursor-default enabled:cursor-pointer"
                    >
                      <Avatar size="sm">
                        {photoUrl ? <Avatar.Image src={photoUrl} alt={name} /> : null}
                        <Avatar.Fallback className="text-xs">
                          {name
                            .split(" ")
                            .map(part => part[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </Avatar.Fallback>
                      </Avatar>
                      <span className="truncate font-medium">{name}</span>
                    </button>
                  </Table.Cell>
                  {SORT_COLUMNS.map(column => {
                    const showTrophy = column.key === sort && sortRank != null && sortRank <= 3;
                    return (
                      <Table.Cell key={column.key} className="text-right tabular-nums">
                        <span className="inline-flex items-center justify-end gap-1.5">
                          {showTrophy && <RankTrophy rank={sortRank} />}
                          <span className={metricValue(snapshot, column.key) == null ? "text-muted-foreground" : ""}>
                            {formatMetric(snapshot, column.key)}
                          </span>
                        </span>
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </section>
  );
}

export type { PlayerSortKey };
